import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  generateMerchantIdsHash,
  getWidgetConfig,
  saveWidgetState,
  shouldReinitializeWidget,
  sortMerchantIds,
} from "../../utils/ifoodWidgetUtils";
import Box from "@mui/material/Box";
import { Button } from "@mui/material";

export default function IFoodWidget({ merchantIds }) {
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const initializationInProgress = useRef(false);
  const lastMerchantIdsHash = useRef(null);
  const checkInterval = useRef(null);
  const widgetConfig = getWidgetConfig();

  const initializeWidget = useCallback(async () => {
    if (initializationInProgress.current) {
      return;
    }

    const sortedMerchantIds = sortMerchantIds(merchantIds);

    if (!window.iFoodWidget || sortedMerchantIds.length === 0) {
      return;
    }

    const currentHash = generateMerchantIdsHash(merchantIds);

    if (lastMerchantIdsHash.current === currentHash && isWidgetReady) {
      return;
    }

    initializationInProgress.current = true;

    try {
      const needsReinitialization = shouldReinitializeWidget(merchantIds);

      if (needsReinitialization || !isWidgetReady) {
        window.iFoodWidget.init({
          widgetId: widgetConfig.widgetId,
          merchantIds: sortedMerchantIds,
          autoShow: false,
        });

        await window.iFoodWidget.ready;

        saveWidgetState(currentHash);
        lastMerchantIdsHash.current = currentHash;
        setIsWidgetReady(true);
        setIsVisible(true);

        console.log("iFood Widget initialized successfully");
      }
    } catch (error) {
      console.error("Error initializing iFood Widget:", error);
      setIsWidgetReady(false);
    } finally {
      initializationInProgress.current = false;
    }
  }, [merchantIds, widgetConfig.widgetId, isWidgetReady]);

  const showWidget = useCallback(() => {
    if (!window.iFoodWidget || !isWidgetReady) {
      console.warn("Widget not ready to show");
      return;
    }

    try {
      window.iFoodWidget.show();
      setIsVisible(true);
    } catch (error) {
      console.error("Error showing widget:", error);
    }
  }, [isWidgetReady]);

  const hideWidget = useCallback(() => {
    if (!window.iFoodWidget || !isWidgetReady) {
      return;
    }

    try {
      window.iFoodWidget.hide();
      setIsVisible(false);
    } catch (error) {
      console.error("Error hiding widget:", error);
    }
  }, [isWidgetReady]);

  const toggleWidget = useCallback(() => {
    if (isVisible) {
      hideWidget();
    } else {
      showWidget();
    }
  }, [isVisible, showWidget, hideWidget]);

  const checkScriptLoaded = useCallback(() => {
    if (window.iFoodWidget && !scriptLoaded) {
      console.log("iFood script detected");
      setScriptLoaded(true);

      if (checkInterval.current) {
        clearInterval(checkInterval.current);
        checkInterval.current = null;
      }
    }
  }, [scriptLoaded]);

  // Effect to monitor script loading
  useEffect(() => {
    if (window.iFoodWidget) {
      setScriptLoaded(true);
      return;
    }

    // Check every 100ms for the script to load
    checkInterval.current = setInterval(checkScriptLoaded, 100);

    // Cleanup after 10 seconds if script doesn't load
    const timeout = setTimeout(() => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
        checkInterval.current = null;
        console.error("iFood script failed to load after 10 seconds");
      }
    }, 10000);

    return () => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
        checkInterval.current = null;
      }
      clearTimeout(timeout);
    };
  }, [checkScriptLoaded]);

  // Effect to initialize widget when script is loaded and merchantIds change
  useEffect(() => {
    if (!scriptLoaded || !merchantIds || merchantIds.length === 0) {
      return;
    }

    const currentHash = generateMerchantIdsHash(merchantIds);
    const hasChangedMerchants =
      lastMerchantIdsHash.current &&
      lastMerchantIdsHash.current !== currentHash;

    if (hasChangedMerchants || !isWidgetReady) {
      initializeWidget();
    }
  }, [scriptLoaded, merchantIds, initializeWidget, isWidgetReady]);

  // Effect to handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isVisible) {
        hideWidget();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isVisible, hideWidget]);

  // Effect to expose toggle function globally for external trigger
  useEffect(() => {
    window.toggleIfoodWidget = toggleWidget;

    return () => {
      delete window.toggleIfoodWidget;
    };
  }, [toggleWidget]);

  return (
    <>
      <Helmet>
        <script
          async
          src="https://widgets.ifood.com.br/widget.js"
          onLoad={() => {
            console.log("iFood script loaded via onLoad");
            setScriptLoaded(true);
          }}
          onError={(error) => {
            console.error("Failed to load iFood script:", error);
          }}
        />
      </Helmet>
      {isWidgetReady && !isVisible && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16, // Adds some space from the bottom edge
            right: 16, // Adds some space from the right edge
            zIndex: 1000, // Ensures it stays on top of other content
          }}
        >
          <Button
            id="ifood-widget-trigger"
            onClick={toggleWidget}
            aria-label="Toggle iFood Widget"
            variant="contained" // Optional: gives the button a background
          >
            ATIVAR IFOOD
          </Button>
        </Box>
      )}
    </>
  );
}
